// ***********************************************************
// pathname: /v1/user-phone/verify
// ***********************************************************
import { useSwrMutationApi } from '@hooks/useSwrApi';
import { useTranslation } from 'next-i18next';

/** Props */
interface SwrUserPhoneVerifyProps {
  locale: string;
  successCallback?: () => void;
  errorCallback?: () => void;
}

/**
 * @description This hook is used for verifying the user's phone number.
 * - `locale` is the language locale
 * - `successCallback` is a function that is called when the request is successful
 * - `errorCallback` is a function that is called when the request fails
 */
export const useSwrUserPhoneVerify = (props: SwrUserPhoneVerifyProps) => {
  // Destructure props
  const {
    locale,
    successCallback,
    errorCallback,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Return hook
  return useSwrMutationApi<object, ApiSchema<'PhoneNumberWrite'>>({
    url: '/v1/user-phone/verify',
    locale,
    fetcherConfig: {
      method: 'POST',
      stringifyPayload: true,
    },
    apiOptions: {
      onSuccess: () => {
        if (successCallback) successCallback();
      },
      onError: () => {
        if (errorCallback) errorCallback();
      },
    },
    toastsOptions: {
      showSuccessToast: true,
      successToastText: t('account.settings.label.verify_code_sent'),
      showErrorToast: true,
      byErrorCodes: [
        { code: '403', text: t('number.confirm.limit.reached') },
      ],
    },
  });
};
